import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ColorHash from 'color-hash';
import classNames from 'classnames';

import Draggable from 'react-draggable';

import { withStyles } from '@material-ui/core/styles';

import Chip from 'components/common/Chip';
import Badge from 'components/common/Badge';

const styles = theme => ({
  rootDraggable: {
    transitionDelay: 'unset',
  },
  iconColorHash: {
    display: 'inline-block',
    width: 8,
    height: 8,
    marginRight: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.A100,
  },
  rootDragged: {
    backgroundColor: theme.palette.grey[200],
    '&:focus': {
      backgroundColor: theme.palette.common.white,
    },
  },
  rootDraggedHighlighted: {
    '&:focus': {
      backgroundColor: theme.palette.primary[500],
    },
  },
  draggable: {
    position: 'relative',
    maxWidth: '100%',
  },
  draggableDragged: {
    zIndex: 10,
  },
  draggableContentWithTransition: {
    transition: theme.transitions.create(['transform'], {
      duration: theme.transitions.duration.short,
    }),
  },
});

const colorHash = new ColorHash();

class FilterField extends Component {
  constructor(props) {
    super(props);

    this.state = {
      dragged: false,
      position: null,
      bounds: null,
      anchorEl: null,
    };

    this.onDraggableDataChange = this.onDraggableDataChange.bind(this);
    this.onStartDrag = this.onStartDrag.bind(this);
    this.onDrag = this.onDrag.bind(this);
    this.onStopDrag = this.onStopDrag.bind(this);
  }

  componentDidMount() {
    this.draggableDataChangeTimeout = setTimeout(() => {
      this.onDraggableDataChange();
    }, 100);
  }

  componentDidUpdate(prevProps) {
    const { timestamp } = this.props;
    if (timestamp !== prevProps.timestamp) {
      this.draggableDataChangeTimeout = setTimeout(() => {
        this.onDraggableDataChange();
      }, 100);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.draggableDataChangeTimeout);
  }

  onDraggableDataChange() {
    const { boundsSelector, index, onDraggableDataChange } = this.props;
    if (boundsSelector) {
      const draggableRefDOMRect = this.draggableRef.getBoundingClientRect();
      const boundsDOMRect = document.querySelector(boundsSelector).getBoundingClientRect();
      const left = Math.round(boundsDOMRect.left - draggableRefDOMRect.left);
      const top = Math.round(boundsDOMRect.top - draggableRefDOMRect.top);
      const right = Math.round((boundsDOMRect.width - draggableRefDOMRect.width) + left);
      const bottom = Math.round((boundsDOMRect.height - draggableRefDOMRect.height) + top);
      const data = {
        bounds: { left, top, right, bottom },
        width: draggableRefDOMRect.width,
        height: draggableRefDOMRect.height,
      };
      const draggableData = {};
      draggableData[index] = data;
      this.setState(data);
      if (onDraggableDataChange) {
        onDraggableDataChange(draggableData);
      }
    }
  }

  onStartDrag(event, data) {
    const { onStartDrag, index } = this.props;
    const { bounds, width, height } = this.state;
    this.onDraggableDataChange();
    this.setState({ dragged: true });
    if (onStartDrag) {
      onStartDrag({
        x: data.x - bounds.left,
        y: data.y - bounds.top,
        width,
        height,
        defaultX: -bounds.left,
        defaultY: -bounds.top,
        index,
      });
    }
  }

  onDrag(event, data) {
    const { onDrag, index } = this.props;
    const { bounds, width, height } = this.state;
    if (onDrag) {
      onDrag({
        x: data.x - bounds.left,
        y: data.y - bounds.top,
        width,
        height,
        defaultX: -bounds.left,
        defaultY: -bounds.top,
        index,
      });
    }
  }

  onStopDrag(event, data) {
    const { onStopDrag, index } = this.props;
    const { bounds, width, height } = this.state;
    this.setState({
      dragged: false,
      position: {
        x: 0,
        y: 0,
      },
    });
    if (onStopDrag) {
      onStopDrag({
        x: data.x - bounds.left,
        y: data.y - bounds.top,
        width,
        height,
        defaultX: -bounds.left,
        defaultY: -bounds.top,
        index,
      });
    }
  }

  render() {
    const {
      input: { value },
      prefix,
      size,
      className,
      onDelete,
      onChipFocus,
      onChipBlur,
      onKeyDown,
      colored,
      highlighted,
      draggable,
      offsetX,
      withTransition,
      index,
      classes,
    } = this.props;
    const { bounds, position, dragged } = this.state;
    const labelText = prefix ? `${prefix}: ${value.label}` : value.label;
    const label = (
      <div>
        {value.startBadge &&
          <Badge
            color={highlighted ? 'white' : 'primary'}
            gutter="right"
          >
            {value.startBadge}
          </Badge>
        }
        {(colored && value.value) &&
          <div
            className={classes.iconColorHash}
            style={{ backgroundColor: colorHash.hex(`00000${value.value}`.slice(-6)) }}
          />
        }
        <span>{labelText}</span>
      </div>
    );
    const chip = (
      <Chip
        data-index={index}
        className={classNames(className, {
          [classes.rootDraggable]: draggable,
          [classes.rootDragged]: dragged,
          [classes.rootDraggedHighlighted]: dragged && highlighted,
        })}
        {...highlighted ? {
          borderColor: 'primary',
          color: 'primary',
        } : {}}
        size={size}
        square
        label={label}
        {...!draggable ? { onClick: onDelete } : {}}
        onDelete={onDelete}
        onFocus={onChipFocus}
        onBlur={onChipBlur}
        onKeyDown={onKeyDown}
      />
    );
    if (draggable) {
      return (
        <Draggable
          // axis="x"
          {...bounds ? { bounds } : {}}
          {...position ? { position } : {}}
          onStart={this.onStartDrag}
          onDrag={this.onDrag}
          onStop={this.onStopDrag}
        >
          <div
            className={classNames(classes.draggable, {
              [classes.draggableDragged]: dragged,
            })}
            ref={(draggableRef) => {
              this.draggableRef = draggableRef;
            }}
          >
            <div
              className={classNames({
                [classes.draggableContentWithTransition]: withTransition,
              })}
              style={{ transform: `translateX(${offsetX}px)` }}
            >
              {chip}
            </div>
          </div>
        </Draggable>
      );
    }
    return (
      chip
    );
  }
}

FilterField.propTypes = {
  prefix: PropTypes.string,
  input: PropTypes.shape({
    value: PropTypes.shape({
      label: PropTypes.string,
    }),
  }).isRequired,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  className: PropTypes.string,
  onDelete: PropTypes.func.isRequired,
  onChipFocus: PropTypes.func,
  onChipBlur: PropTypes.func,
  colored: PropTypes.bool,
  highlighted: PropTypes.bool,
  draggable: PropTypes.bool,
  onStartDrag: PropTypes.func,
  onDrag: PropTypes.func,
  onStopDrag: PropTypes.func,
  onDraggableDataChange: PropTypes.func,
  index: PropTypes.number.isRequired,
  timestamp: PropTypes.number,
  boundsSelector: PropTypes.string,
  offsetX: PropTypes.number,
  withTransition: PropTypes.bool,
  onKeyDown: PropTypes.func,
  classes: PropTypes.shape({
    iconColorHash: PropTypes.string.isRequired,
    rootDraggable: PropTypes.string.isRequired,
    rootDragged: PropTypes.string.isRequired,
    rootDraggedHighlighted: PropTypes.string.isRequired,
    draggable: PropTypes.string.isRequired,
    draggableDragged: PropTypes.string.isRequired,
    draggableContentWithTransition: PropTypes.string.isRequired,
  }).isRequired,
};

FilterField.defaultProps = {
  className: '',
  prefix: null,
  colored: false,
  highlighted: false,
  draggable: false,
  size: 'md',
  onChipFocus: () => {},
  onChipBlur: () => {},
  onStartDrag: null,
  onDrag: null,
  onStopDrag: null,
  boundsSelector: null,
  onDraggableDataChange: null,
  offsetX: 0,
  withTransition: false,
  onKeyDown: () => {},
  timestamp: null,
};

export default withStyles(styles)(FilterField);



// WEBPACK FOOTER //
// ./src/components/fields/FilterField.js